import {
  breakpointSizes,
  getBreakpointDown,
  getBreakpointOnly,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import AccountingTooltip from 'src/components/AccountingTooltip/AccountingTooltip'
import AttachmentsList from 'src/components/AttachmentsList/AttachmentsList'
import CollapsibleMiddleBox from 'src/components/CollapsibleMiddleBox/CollapsibleMiddleBox'
import Trns from 'src/localization/Trns'
import { VirtualCardSecureModel } from 'src/models/virtualCard.models'
import {
  AutomationRuleListSortField,
  useAutomationRuleList,
} from 'src/queries/organizations/automationRules.queries'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { css } from 'styled-components'

interface VirtualCardAccountingBoxProps {
  virtualCard: VirtualCardSecureModel
  openLeftMdUp: boolean
  openLeftSmDown: boolean
  hasIntegrations: boolean
}

const VirtualCardAccountingBox: React.FC<VirtualCardAccountingBoxProps> = (
  props,
): React.ReactElement => {
  const { data: expCatSchema } = useQueryExpenseCategories({
    filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
  })
  const { data: virtualCardRules } = useAutomationRuleList({
    filters: {
      sortField: AutomationRuleListSortField.CreatedAt,
      sortDirection: 'DESC',
      fundingSourceIds: [props.virtualCard?.id ?? ''],
    },
    enabled: props.virtualCard?.id !== undefined,
  })

  const mappingsAlphaSorted =
    expCatSchema
      ?.map((expCat) => {
        // TODO: currently assuming only one rule per VCN
        const mappings = virtualCardRules?.[0]?.mappings
        return mappings?.find(
          (mapping) => mapping.expenseCategory.id === expCat.id,
        )
      })
      .filter((mapping) => mapping !== undefined) || []

  const expenseDetailCount = mappingsAlphaSorted.length ?? 0

  const countExpressions = {
    expCatCount: `${expenseDetailCount}`,
    expDetailCount: `${expenseDetailCount}`,
    attachmentCount: `${props.virtualCard.receiptAttachments?.length}`,
  }

  const expenseCatCopyForHeader = (): string => {
    if (expenseDetailCount === 1) {
      return props.virtualCard.receiptAttachments?.length !== 1
        ? '${expCatCount} Expense Category, ${attachmentCount} Attachments'
        : '${expCatCount} Expense Category, ${attachmentCount} Attachment'
    } else {
      return props.virtualCard.receiptAttachments?.length === 1
        ? '${expCatCount} Expense Categories, ${attachmentCount} Attachment'
        : '${expCatCount} Expense Categories, ${attachmentCount} Attachments'
    }
  }

  const topContent = (
    <div
      css={css`
        display: flex;
        justify-content: space-between;
        padding: 36px 40px;

        ${getBreakpointOnly(breakpointSizes.XXSM)(css`
          padding: 36px 20px;
        `)};
      `}
    >
      <div
        css={css`
          display: flex;
        `}
      >
        <span
          css={css`
            margin-right: 12px;
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
          `}
        >
          <Trns source={'Accounting'} />
        </span>
        <span
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.highlight,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <Trns
            source={expenseCatCopyForHeader()}
            expressions={countExpressions}
          />
        </span>
      </div>
      <AccountingTooltip hideSmDown openLeft={props.openLeftMdUp} />
      <AccountingTooltip hideMdUp openLeft={props.openLeftSmDown} />
    </div>
  )

  const middleContent = (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: 0px 40px;

        ${getBreakpointDown(breakpointSizes.XXSM)(css`
          padding: 0px 20px;
        `)}
      `}
    >
      <div
        data-testid="accounting-expenseCatsSection"
        css={css`
          display: flex;
          flex-direction: column;
          margin-top: 36px;
          &:last-child {
            margin-bottom: 36px;
          }
        `}
      >
        <span
          css={css`
            margin-bottom: 12px;
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <Trns source={'Expense Categories'} />
        </span>
        {expenseDetailCount > 0 ? (
          <div
            data-testid={'expenseDetails'}
            css={css`
              display: flex;
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
              flex-direction: column;
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 12px;
              `}
            >
              {mappingsAlphaSorted.map((mapping, i) =>
                mapping?.expenseLabel ? (
                  <div key={i}>
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                      `}
                    >
                      {`${mapping.expenseCategory.name}: `}
                    </span>
                    <span
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      {mapping.expenseLabel.name +
                        (mapping.expenseLabel.subName
                          ? `- (${mapping.expenseLabel.subName})`
                          : '')}
                    </span>
                  </div>
                ) : (
                  <></>
                ),
              )}
            </div>
          </div>
        ) : (
          <span
            css={css`
              display: flex;
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};

              &.expense-category-fields {
                flex-direction: column;
              }
            `}
          >
            <Trns source={'None'} />
          </span>
        )}
      </div>
      <div
        data-testid="accounting-attachmentsSection"
        css={css`
          display: flex;
          flex-direction: column;
          margin-top: 36px;
          &:last-child {
            margin-bottom: 36px;
          }
        `}
      >
        <span
          css={css`
            margin-bottom: 12px;
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <Trns source={'Attachments'} />
        </span>
        {props.virtualCard.receiptAttachments?.length !== undefined &&
          props.virtualCard.receiptAttachments.length > 0 && (
            <AttachmentsList
              attachments={props.virtualCard.receiptAttachments}
            />
          )}
        {props.virtualCard.receiptAttachments?.length === 0 && (
          <span
            data-count={0}
            css={css`
              display: flex;
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};

              &.expense-category-fields {
                flex-direction: column;
              }
            `}
          >
            <Trns source={'None'} />
          </span>
        )}
      </div>
    </div>
  )

  const closedBottomLabel = <Trns source={'Accounting Details'} />
  const openBottomLabel = <Trns source={'Hide Accounting Details'} />

  return (
    <CollapsibleMiddleBox
      className={'virtualCardAccountingDetails'}
      topContent={topContent}
      middleContent={middleContent}
      bottomContent={{
        closedBottomLabel: closedBottomLabel,
        openBottomLabel: openBottomLabel,
      }}
    />
  )
}

export default VirtualCardAccountingBox
